import { Amplify, type ResourcesConfig } from 'aws-amplify';

export type CognitoEnvironment = {
    VITE_AUTH_USER_POOL_ID?: string;
    VITE_AUTH_USER_POOL_CLIENT_ID?: string;
    VITE_AUTH_IDENTITY_POOL_ID?: string;
};

const isUnsubstitutedPlaceholder = (value: string) => /^%.+%$/.test(value);

const readRequired = (name: keyof CognitoEnvironment, environment: CognitoEnvironment): string => {
    const value = environment[name]?.trim() ?? '';

    if (value === '') {
        throw new Error(`${name} is not set. Cognito authentication needs it; check the .env file for the build mode.`);
    }

    if (isUnsubstitutedPlaceholder(value)) {
        throw new Error(
            `${name} still holds the build placeholder ${value}. The CI job that substitutes it did not run, ` +
                'or the matching CI variable is undefined for this environment.'
        );
    }

    return value;
};

const readOptional = (name: keyof CognitoEnvironment, environment: CognitoEnvironment): string | undefined => {
    const value = environment[name]?.trim() ?? '';

    return value === '' || isUnsubstitutedPlaceholder(value) ? undefined : value;
};

export const buildAmplifyConfig = (environment: CognitoEnvironment): ResourcesConfig => {
    // Nothing about sign up is configured: the pool disables self sign up and the app has no such flow.
    const userPool = {
        userPoolId: readRequired('VITE_AUTH_USER_POOL_ID', environment),
        userPoolClientId: readRequired('VITE_AUTH_USER_POOL_CLIENT_ID', environment),
    };

    const identityPoolId = readOptional('VITE_AUTH_IDENTITY_POOL_ID', environment);

    if (identityPoolId === undefined) {
        return { Auth: { Cognito: userPool } };
    }

    return { Auth: { Cognito: { ...userPool, identityPoolId } } };
};

export const configureAmplify = (environment: CognitoEnvironment): void => {
    Amplify.configure(buildAmplifyConfig(environment));
};
